import { useAgent } from 'agents/react';
import { Effect } from 'effect';
import { useCallback, useEffect, useState } from 'react';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import { normalizeOkraRpcArgs } from './rpc-schema';
import type { DocumentAgentMethods, DocumentAgentState } from './agent-types';

type QueryObject = Record<string, string | null>;
type QueryResolver = (name: string) => QueryObject | Promise<QueryObject>;
type MethodArgs<Methods, K extends keyof Methods> = Methods[K] extends (...args: infer Args) => any
  ? Args
  : never;
type MethodResult<Methods, K extends keyof Methods> = Methods[K] extends (
  ...args: any[]
) => infer Result
  ? Awaited<Result>
  : never;

type AgentShape<State, Methods extends object> = Methods & {
  get state(): State;
};

export interface OkraAuthEndpointRequest {
  docId: string;
}

export interface OkraAuthEndpointResponse {
  token: string;
  [key: string]: unknown;
}

export interface CreateOkraContextOptions {
  host?: string;
  agent: string;
  authEndpoint?: string;
  query?: QueryResolver;
  cacheTtl?: number;
}

export type OkraDocumentAgent<State, Methods extends object> = ReturnType<
  typeof useAgent<AgentShape<State, Methods>, State>
>;

/**
 * Native auth-endpoint transport: POST `{ docId }`, expect `{ token }`.
 *
 * The tagged failure carries only the operation label — never the token, the
 * response body, or the endpoint's headers. The thrown compatibility cause
 * (with the SDK's historical message) is restored by {@link runOkraHookEffect},
 * so `useAgent`'s query resolver sees the identical rejection it always has.
 */
export function fetchAuthEndpointEffect(
  endpoint: string,
  name: string,
): Effect.Effect<QueryObject, OkraHookError> {
  return Effect.gen(function* () {
    const response = yield* Effect.tryPromise({
      try: () =>
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docId: name } satisfies OkraAuthEndpointRequest),
        }),
      catch: (cause) => okraHookFailure('authEndpoint', 'Okra auth endpoint request failed', cause),
    });

    if (!response.ok) {
      return yield* Effect.fail(
        okraHookFailure(
          'authEndpoint',
          'Okra auth endpoint returned a non-OK status',
          new Error(`Okra auth endpoint failed: ${response.status}`),
        ),
      );
    }

    const body = yield* Effect.tryPromise({
      try: () => response.json() as Promise<OkraAuthEndpointResponse>,
      catch: (cause) =>
        okraHookFailure('authEndpoint', 'Okra auth endpoint response was not valid JSON', cause),
    });

    if (!body.token) {
      return yield* Effect.fail(
        okraHookFailure(
          'authEndpoint',
          'Okra auth endpoint response was missing a token',
          new Error('Okra auth endpoint response missing token'),
        ),
      );
    }

    return { token: body.token };
  });
}

function fetchAuthEndpoint(endpoint: string, name: string): Promise<QueryObject> {
  return runOkraHookEffect(fetchAuthEndpointEffect(endpoint, name));
}

export function createOkraContext<
  State = DocumentAgentState,
  Methods extends object = DocumentAgentMethods,
>(cfg: CreateOkraContextOptions) {
  function useDocument(name: string | null | undefined): OkraDocumentAgent<State, Methods> {
    const resolvedName = name || '_idle';
    const query = useCallback(async () => {
      const [endpointQuery, customQuery] = await Promise.all([
        cfg.authEndpoint ? fetchAuthEndpoint(cfg.authEndpoint, resolvedName) : Promise.resolve({}),
        cfg.query ? cfg.query(resolvedName) : Promise.resolve({}),
      ]);
      return { ...endpointQuery, ...customQuery };
    }, [resolvedName]);

    return useAgent<AgentShape<State, Methods>, State>({
      agent: cfg.agent,
      name: resolvedName,
      host: cfg.host,
      startClosed: !name,
      query: cfg.authEndpoint || cfg.query ? query : undefined,
      queryDeps: [resolvedName, cfg.authEndpoint],
      cacheTtl: cfg.cacheTtl ?? 30_000,
    });
  }

  function useDocumentSlice<T>(
    name: string | null | undefined,
    selector: (state: State) => T,
  ): T | undefined {
    const document = useDocument(name);
    const [selected, setSelected] = useState<T | undefined>(() =>
      document.state ? selector(document.state) : undefined,
    );

    useEffect(() => {
      if (!document.state) {
        setSelected(undefined);
        return;
      }
      const next = selector(document.state);
      setSelected((prev) => (Object.is(prev, next) ? prev : next));
    }, [document.state, selector]);

    return selected;
  }

  function useDocumentAction<K extends keyof Methods>(
    name: string | null | undefined,
    method: K,
  ): (...args: MethodArgs<Methods, K>) => Promise<MethodResult<Methods, K>> {
    const document = useDocument(name);
    return useCallback(
      (...args: MethodArgs<Methods, K>) => {
        const call = document.call as (method: string, args?: unknown[]) => Promise<unknown>;
        return call(String(method), normalizeOkraRpcArgs(args as unknown[])) as Promise<
          MethodResult<Methods, K>
        >;
      },
      [document, method],
    );
  }

  function useDocumentEvents(
    name: string | null | undefined,
    handler: (event: unknown) => void,
  ): void {
    const document = useDocument(name);
    useEffect(() => {
      if (!name) return;
      const onMessage = (event: MessageEvent) => {
        try {
          handler(JSON.parse(String(event.data)));
        } catch {
          handler(event.data);
        }
      };
      document.addEventListener('message', onMessage);
      return () => document.removeEventListener('message', onMessage);
    }, [document, handler, name]);
  }

  return {
    useDocument,
    useDocumentSlice,
    useDocumentAction,
    useDocumentEvents,
  };
}
